import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import TextField, { BaseTextFieldProps, TextFieldVariants } from "@mui/material/TextField";
import Name, { InvalidNameError } from "_shared/names/name";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

interface NameInputContainerProps {
  direction?: "row" | "column";
}

const NameInputContainer = styled(Stack)<NameInputContainerProps>(({ theme, direction = "row" }) => ({
  flexDirection: direction,
  gap: direction === "row" ? theme.spacing(1.5) : 0,
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: 0,
  },
}));
const InputField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    marginRight: 0,
    width: "100%",
  },
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
}));

interface NameInputProps extends BaseTextFieldProps {
  idPrefix?: string;
  className?: string;
  variant?: TextFieldVariants;
  initialValue?: Name;
  /**
   * This method is called when the name instance is modified. This callback is only
   * registered initially, to reregister the handler, change the key
   * @returns ({@link Name}, undefined) when successful
   * @returns (undefined, errorMessage: string) when unsuccessful
   */
  onChange?: (name?: Name, errorMessage?: string) => void;
  direction?: "row" | "column";
}

export default function NameInput(props: Readonly<NameInputProps>) {
  const { idPrefix, variant, initialValue, onChange, direction, ...otherProps } = props;

  const [firstNameText, setFirstNameText] = useState<string>(initialValue?.firstName ?? "");
  const [lastNameText, setLastNameText] = useState<string>(initialValue?.lastName ?? "");
  const [firstNameError, setFirstNameError] = useState<string>("");
  const [lastNameError, setLastNameError] = useState<string>("");

  const firstNameIsDirty = useRef<boolean>(false);
  const lastNameIsDirty = useRef<boolean>(false);

  const debouncedValidate = useMemo(() => 
    debounce((firstName?: string, lastName?: string, firstNameIsDirty?: boolean, lastNameIsDirty?: boolean) => {
      let newName: Name | undefined;
      
      if(!firstNameIsDirty || !lastNameIsDirty) return;
      try {
        newName = buildName(firstName, lastName);
        setFirstNameError("");
        setLastNameError("");
        onChange?.(newName, undefined);
      } catch (error) {
        if (!(error instanceof InvalidNameError)) throw error;
        setFirstNameError(error.firstNameErrorMessage);
        setLastNameError(error.lastNameErrorMessage);
        onChange?.(newName, error.message);
      }
    }, 300),
  // We cannot guarantee onChange being stable, if it changes, change the key to recreate the component
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  useEffect(() => {
    debouncedValidate(firstNameText, lastNameText, firstNameIsDirty.current, lastNameIsDirty.current);
    return () => debouncedValidate.cancel();
  }, [firstNameText, lastNameText, debouncedValidate]);

  return (
    <NameInputContainer direction={direction}>
      <InputField
        id={idPrefix ? idPrefix + "-first-name-input" : "first-name-input"}
        label="First Name"
        onChange={(event) => {
          setFirstNameText(prev => {
            if(firstNameIsDirty.current) return event.target.value;
            if(prev !== event.target.value)
            {
              firstNameIsDirty.current = true;
              return event.target.value;
            }
            return prev;
          })
        }}
        required
        value={firstNameText}
        error={!!firstNameError}
        helperText={firstNameError}
        variant={variant}
        autoComplete="given-name"
        {...otherProps}
      />
      <InputField
        id={idPrefix ? idPrefix + "-last-name-input" : "last-name-input"}
        label="Last Name"
        onChange={(event) => {
          setLastNameText(prev => {
            if(lastNameIsDirty.current) return event.target.value;
            if(prev !== event.target.value)
            {
              lastNameIsDirty.current = true;
              return event.target.value;
            }
            return prev;
          });
        }}
        required
        value={lastNameText}
        error={!!lastNameError}
        helperText={lastNameError}
        variant={variant}
        autoComplete="family-name"
        {...otherProps}
      />
    </NameInputContainer>
  );
}

function buildName(firstName?: string, lastName?: string): Name | undefined {
  try {
    return Name.create({ params: { firstName, lastName }, options: { silenceSchemaErrors: true } }) ?? undefined;
  } catch (error) {
    if (error instanceof InvalidNameError) {
      throw error;
    }
  }
}
